// src/components/Login.jsx
import React, { useEffect, useState } from "react";
import {
  Box,
  Paper,
  Stack,
  Typography,
  TextField,
  Button,
  Divider,
  Alert,
  CircularProgress,
} from "@mui/material";

import {
  getAuth,
  onAuthStateChanged,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  GoogleAuthProvider,
  signInWithPopup,
} from "firebase/auth";

/**
 * Usage:
 * <Login><ProtectedComponent /></Login>
 *
 * Behaviour:
 * - Shows loading while auth state is resolving
 * - If logged in, renders children
 * - If not logged in, shows login UI
 */
export default function Login({ children }) {
  const auth = getAuth();

  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);

  const [mode, setMode] = useState("login"); // "login" | "signup"
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (u) => {
      setUser(u || null);
      setLoading(false);
    });
    return () => unsub();
  }, [auth]);

  const normaliseError = (e) => {
    const msg = e?.message || String(e);
    return msg.replace(/^Firebase:\s*/i, "").replace(/\s*\(auth\/.*?\)\s*/i, "");
  };

  const handleEmailAuth = async () => {
    setError("");
    setSubmitting(true);
    try {
      if (!email.trim()) throw new Error("Email is required.");
      if (!password) throw new Error("Password is required.");

      if (mode === "signup") {
        await createUserWithEmailAndPassword(auth, email.trim(), password);
      } else {
        await signInWithEmailAndPassword(auth, email.trim(), password);
      }
    } catch (e) {
      setError(normaliseError(e));
    } finally {
      setSubmitting(false);
    }
  };

  const handleGoogle = async () => {
    setError("");
    setSubmitting(true);
    try {
      const provider = new GoogleAuthProvider();
      await signInWithPopup(auth, provider);
    } catch (e) {
      setError(normaliseError(e));
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Box
        sx={{
          minHeight: "60vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Stack spacing={2} alignItems="center">
          <CircularProgress />
          <Typography variant="body2" sx={{ opacity: 0.8 }}>
            Loading…
          </Typography>
        </Stack>
      </Box>
    );
  }

  if (user) return <>{children}</>;

  return (
    <Box
      sx={{
        minHeight: "60vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        px: 2,
      }}
    >
      <Paper sx={{ width: "100%", maxWidth: 420, p: 3 }}>
        <Stack spacing={2}>
          <Typography variant="h6">
            {mode === "signup" ? "Create account" : "Sign in"}
          </Typography>

          {error ? <Alert severity="error">{error}</Alert> : null}

          <TextField
            label="Email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            autoComplete="email"
            fullWidth
          />

          <TextField
            label="Password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            type="password"
            autoComplete={mode === "signup" ? "new-password" : "current-password"}
            fullWidth
          />

          <Button
            variant="contained"
            onClick={handleEmailAuth}
            disabled={submitting}
            fullWidth
          >
            {submitting
              ? "Please wait…"
              : mode === "signup"
              ? "Create account"
              : "Sign in"}
          </Button>

          <Divider>or</Divider>

          <Button
            variant="outlined"
            onClick={handleGoogle}
            disabled={submitting}
            fullWidth
          >
            Continue with Google
          </Button>

          <Divider />

          <Stack direction="row" spacing={1} justifyContent="center">
            <Typography variant="body2" sx={{ opacity: 0.8 }}>
              {mode === "signup" ? "Already have an account?" : "No account yet?"}
            </Typography>
            <Button
              variant="text"
              size="small"
              onClick={() => {
                setError("");
                setMode(mode === "signup" ? "login" : "signup");
              }}
              disabled={submitting}
              sx={{ minWidth: "auto", px: 1 }}
            >
              {mode === "signup" ? "Sign in" : "Create one"}
            </Button>
          </Stack>
        </Stack>
      </Paper>
    </Box>
  );
}
